import {useEffect, useMemo, useRef, useState} from "react";
import {DrawFrmApp, DrawFrmAppOpts} from "./drawfrm/DrawFrmApp";
import {LoadingScene} from "./scenes/LoadingScene";
import {api} from "../../../../api/API";
import {AnchorProducts, SlotOpening} from "../../../../api/ElevationTool";
import {useProjectId} from "../ProjectName";
import {Opening} from "../../../../api/Openings";
import { ErrorScene } from "./scenes/ErrorScene";
import {MdFrameProfileType, MdFrameSpec, wireDoorsItem} from "./model/frame/MdFrameSpec";
import {parseDrawingUnits} from "./util/DrawingUnits";
import {DimensionEditor, formatDim} from "./components/DimensionEditor";
import {ShowError} from "./components/ShowError";
import {Project} from "../../../../api/Projects";
import {useNavigate} from "react-router-dom";
import {EventEmitter2} from "./util/EventEmitter2";
import {FrameAllScene} from "./scenes/frameAll/FrameAllScene";
import {backgroundBlue, gridBlue0, gridBlue1, inches, mm} from "./const";
import {Scene} from "./drawfrm/Scene";
import {TopDownScene} from "./scenes/frameProfile/TopDownScene";
import {FrameProfileScene} from "./scenes/frameProfile/FrameProfileScene";
import {MdDoorSpec} from "./model/door/MdDoorSpec";
import {MdHardware, HardwareReconcileReport} from "./model/hardware/MdHardware";
import {HardwareGroupWithContent} from "../../../../api/Hardware";
import {FrameGridScene, FrameGridView} from "./scenes/frameGrid/FrameGridScene";
import {DoorElevScene} from "./scenes/doorElev/DoorElevScene";
import {ExportScene} from "./scenes/export/ExportScene";
import {ScreenExportScene} from "./scenes/export/ScreenExportScene";
import {DoorExportScene} from "./scenes/export/DoorExportScene";
import {HardwareExportScene} from "./scenes/export/HardwareExportScene";
import {CutPlanExportScene} from "./scenes/export/CutPlanExportScene";
import {LocationsScene} from "./scenes/locations/LocationsScene";
import {MenuBar} from "./menubar/MenuBar";
import {StatusBar} from "./StatusBar";
import {HardwareSidebar} from "./components/HardwareSidebar";
import {DoorSidebar} from "./components/DoorSidebar";
import {StatusHintProvider} from "./StatusHintContext";
import {MdDisplayView, MdViewSettings, Workspace, workspaceHomeView, workspaceViews} from "./model/view/MdViewSettings";
import {LayoutDebug} from "./scenes/frameGrid/Layout";
import {FrmVar} from "./drawfrm/FrmVar";
import {screenFromData, doorFromData, hardwareFromData, screenToData, doorToData, hardwareToData} from "./serialize";
import {identityFromOpening} from "./serialize/frmVar";
import {ScreenElevation, DoorElevation, HardwareElevation, ThreePartElevation, ElevationDimensions, ElevationSaveSlot, SavePreflight} from "../../../../api/elevationTool/ThreePartElevation";
import {FrameDesign} from "../../../../api/elevationTool/ScreenElevationData";
import {ElevationSnapshot, SnapshotDimensions, UndoHistory} from "./UndoHistory";
import {SelectedGlassLabels} from "./shared-2d/GlassLabel";
import {DragStatus} from "./scenes/frameGrid/Util";
import {anchorSetupAllowed, startAnchorSetup} from "./menubar/AnchorSetupTab";

// the current name of each of the three elevations, or null when not yet saved/named.
export type ElevationNames = {screen: string | null; door: string | null; hardware: string | null};

// which submittal export a scene renders. Frame == screen.
export type ExportKind = "screen" | "door" | "hardware" | "cutplan";

// build the export scene for a given kind, bound to the app. Shared by the Debug-tab
// preview (showExport) and the off-screen render-on-save path.
export function exportSceneFor(kind: ExportKind, app: ElevationApp) {
    switch(kind) {
        case "screen": return new ScreenExportScene(app);
        case "door": return new DoorExportScene(app);
        case "hardware": return new HardwareExportScene(app);
        case "cutplan": return new CutPlanExportScene(app);
    }
}

// the display view that previews each export kind.
const exportViews: Record<ExportKind, MdDisplayView> = {
    screen: "screen-export",
    door: "door-export",
    hardware: "hardware-export",
    cutplan: "cutplan-export",
};

// one of the three elevations' naming state for the name prompt: its display label and the
// name it already has (null = needs naming on this save).
export type ElevationNamingItem = {kind: "screen" | "door" | "hardware"; label: string; existingName: string | null};

// which of the three elevations a save should write. A workspace saves its own; the
// save popover lets the user add the others when they have unsaved changes.
export type ElevationSelection = {screen: boolean; door: boolean; hardware: boolean};

// each elevation's identity + design body, JSON-encoded, for comparing the live models against
// the last load/save. null reads as unsaved regardless of the live models.
type ElevationDesigns = {screen: string | null; door: string | null; hardware: string | null};

// the elevation a workspace saves by default: its own.
export function workspaceSelection(workspace: Workspace): ElevationSelection {
    switch(workspace) {
        case "screen": return {screen: true, door: false, hardware: false};
        case "door": return {screen: false, door: true, hardware: false};
        case "hardware": return {screen: false, door: false, hardware: true};
    }
}

// an elevation saved for this opening only, under a new name. newRow copies a shared elevation
// into a fresh row; otherwise the existing row is renamed.
export type ElevationRename = {name: string; newRow: boolean};
export type ElevationRenames = {screen?: ElevationRename; door?: ElevationRename; hardware?: ElevationRename};

// combine two elevation selections (selected in either => selected).
export function unionSelection(a: ElevationSelection, b: ElevationSelection): ElevationSelection {
    return {screen: a.screen || b.screen, door: a.door || b.door, hardware: a.hardware || b.hardware};
}

export class ElevationApp {
    draw: DrawFrmApp;
    rootEl: HTMLDivElement | null = null;

    project: Project | null = null;
    opening: Opening | null = null;
    hardwareGroup: HardwareGroupWithContent | null = null;

    // null in production mode; the quote alternative id in quote mode. Set at load and
    // read by save to target a quote opening (setForQuoteOpening + alternative scope)
    // instead of a production opening (setForOpening, project scope). In quote mode the
    // opening above holds a QuoteOpening — the elevation models only read the fields the
    // two types share, so it's loaded through the same code path.
    alternative: number | null = null;

    frameSpec: MdFrameSpec = new MdFrameSpec();
    doorSpec: MdDoorSpec = new MdDoorSpec();
    hardware: MdHardware = new MdHardware();

    // the drw rows this opening currently links to (by id/name), or null when the spec
    // still uses the legacy file. Set from getForOpening at load and refreshed from the
    // upsert response on save; drives update-vs-insert and whether save must prompt for
    // a name.
    loadedScreen: ScreenElevation | null = null;
    loadedDoor: DoorElevation | null = null;
    loadedHardware: HardwareElevation | null = null;

    // set in edit-global mode (?edit-global=): the global door elevation being edited, by its
    // drwDoorElevation ddl row. The opening is only for display, and save writes only the door.
    editGlobal: {ddl: number; name: string} | null = null;

    // the error from the last load(), or null when it succeeded. Read by callers that can't see
    // the ErrorScene (see rendering/OffscreenElevationRenderer).
    loadError: unknown = null;

    private lastLoadProps: Parameters<ElevationApp["load"]>[0] | null = null;

    // the current name of each of the three elevations (null = not yet saved/named). Drives
    // the Save-bar chip and the name prompt; re-emitted on load and after save.
    elevationNames: EventEmitter2<ElevationNames> = new EventEmitter2<ElevationNames>(
        {screen: null, door: null, hardware: null}, "elev.names");

    // the autoSaveName we've already auto-saved for (see load) — guards against re-saving
    // if load re-runs with the same param.
    private autoSaveNameDone: string | null = null;

    // the nominal sizes the models were built with, in drawing units, captured after each
    // load/save. save() diffs the live vars against these to tell a real edit from a
    // parse/format round-trip; an isModified() check can't serve here because
    // inActiveDoorWidth carries overrideIsModified(true) and so always reads as modified.
    private loadedDims: SnapshotDimensions | null = null;

    // each elevation's design as last loaded or saved. unsavedSelection() diffs the live
    // models against it, so an edit in one workspace that changes another workspace's
    // elevation (e.g. a lock-bore drag in the door view) is still offered on save.
    private savedDesigns: ElevationDesigns = {screen: null, door: null, hardware: null};

    // unsaved-changes indicator. Set by the models' change events on each edit; after a
    // save or undo it is recomputed from unsavedSelection(), so a partial save leaves it
    // set while another elevation still has changes.
    dirty: EventEmitter2<boolean> = new EventEmitter2<boolean>(false, "elev.dirty");
    private dirtyUnsubs: (() => void)[] = [];

    viewSettings = new MdViewSettings();

    history = new UndoHistory(() => this.snapshot(), snap => this.restoreSnapshot(snap));

    // the human-readable result of the most recent frame-grid solve (constraints +
    // solved variable values), surfaced by the StatusBar's variable/formula debug
    // popover. null until the frame-grid view has laid out at least once.
    debugLayout: EventEmitter2<LayoutDebug | null> = new EventEmitter2<LayoutDebug | null>(null, "elev.debugLayout");

    ready: EventEmitter2<boolean> = new EventEmitter2<boolean>(false, "elev.ready");

    selectedGlassLabels = new SelectedGlassLabels();

    // what the stick being dragged will do on release (e.g. a minimum not met), shown in
    // the StatusBar. null when no drag says anything.
    dragStatus = new EventEmitter2<DragStatus | null>(null, "elev.dragStatus");

    // true while a stick drag is in progress. Set by StickMaterial; the frame grid
    // waits for the release before it shrinks the steps (see normalizeSteps).
    dragging = false;

    // the frame grid currently on the canvas, or null. Set by the view itself; read by
    // the Debug tab's layout copy.
    frameGrid: FrameGridView | null = null;

    // the result of reconciling a loaded hardware elevation against the opening's current
    // hardware group (drop out-of-group placements/overrides, auto-place missing primaries).
    // Emitted after load when the design drifted from the group; the MenuBar surfaces it in a
    // review dialog. null when nothing changed (or before a load).
    hardwareReconcile: EventEmitter2<HardwareReconcileReport | null> = new EventEmitter2<HardwareReconcileReport | null>(null, "elev.hardwareReconcile");

    // the door drawn in each filled secondary door slot, by slot name (see loadSlotDoors).
    slotDoors = new Map<string, SlotDoor>();

    // multi-opening screens. For a primary opening: the openings on its frame and the openings
    // that could fill a door slot (null before load, or when this opening isn't a primary).
    slotOpenings = new EventEmitter2<{linked: SlotOpening[]; candidates: SlotOpening[]} | null>(null, "elev.slotOpenings");
    // the opening picked for each door slot (by slot name) since the last save. Sent as
    // slotLinks with the next screen save.
    pendingSlotLinks = new EventEmitter2<Map<string, SlotOpening>>(new Map(), "elev.pendingSlotLinks");
    // set when the loaded opening is a secondary: it fills a door slot on another opening's
    // frame, so its screen is edited on that opening.
    secondaryOf = new EventEmitter2<{primary: number; primaryName: string; slot: string} | null>(null, "elev.secondaryOf");

    // the products a frame anchor section can use and the company default. null before load.
    anchorProducts = new EventEmitter2<AnchorProducts | null>(null, "elev.anchorProducts");

    // view only (see DrawFrmAppOpts.readOnly): the scenes let the user look and select, not edit.
    get readOnly(): boolean {
        return this.draw.readOnly;
    }

    constructor(container: HTMLDivElement, opts: DrawFrmAppOpts) {
        this.rootEl = container;
        this.draw = new DrawFrmApp(container, opts);
        this.draw.renderDimension = props => {
            return <DimensionEditor {...props} sys={this.project?.unitSystem || "imperial"} />;
        };
        this.draw.formatDimension = (value: number) => {
            const sys = this.project?.unitSystem || "imperial";
            if(sys === "metric") {
                const out = Math.round(value / mm).toString();
                return value < 3 * inches ? out : out + " mm";
            }

            return formatDim(value, sys);
        };
        this.draw.renderError = ShowError;
        this.draw.setBackground(backgroundBlue);
        this.draw.setGrid({
            spacing: inches,    // 1" minor lines
            majorEvery: 5,      // heavier line every 5"
            minorColor: gridBlue1,
            majorColor: gridBlue0,
        });
        this.draw.showScene(new LoadingScene());
        this.ready.emit(false);

        // @ts-ignore
        window["zzDrawApp"] = this;
    }

    dumpState() {
        console.log(JSON.stringify({
            jambDepth: this.frameSpec.jambDepth.getDebug(),
            doorThickness: this.frameSpec.doorFrameProfile.doorThickness.getDebug(),
            frameStopToDoorGap: this.frameSpec.frameStopToDoorGap.getDebug(),
            frameJambToDoorGap: this.frameSpec.frameJambToDoorGap.getDebug(),
            jambFace: this.frameSpec.jambFace.getDebug(),
            rabbetDepth: this.frameSpec.doorFrameProfile.rabbetDepth.getDebug(),
        }, null, 2));
    }

    async load(props: {
        projectId: number,
        // the production opening id (production mode). Ignored when quoteOpening is set.
        opening: string,
        // the quote opening id + its alternative (quote mode). When both are set the tool
        // loads/saves against the quote opening instead of a production opening.
        quoteOpening?: string,
        alternative?: number,
        // which elevation the tool opens focused on (from the ?workspace= url param).
        // Sets the initial ribbon tab + landing view; "screen" is the default.
        workspace: Workspace,
        // when set (via the openings-grid "draw new" free-solo option), auto-save the
        // freshly-loaded opening's elevations under this name once the tool opens. The
        // opening was just unlinked, so its specs are fresh and get created with this name
        // (any still-linked specs keep their own name).
        autoSaveName?: string,
        // the drwDoorElevation ddl id of a global door elevation to edit (from /projects/settings).
        editGlobal?: number,
    }) {

        this.loadError = null;
        this.lastLoadProps = props;

        try {
            this.draw.showScene(new LoadingScene());

            const anchorProductsP = api.elevationTool.anchorProducts();
            const pr = await api.projects.get(props.projectId);

            const quoteMode = props.alternative != null && !!props.quoteOpening;
            const openingIdStr = quoteMode ? props.quoteOpening! : props.opening;

            if (openingIdStr) {
                const openingId = parseInt(openingIdStr);

                let opening: Opening;
                let hardware: HardwareGroupWithContent | null;
                let threePart: ThreePartElevation;

                if (quoteMode) {
                    const alternative = props.alternative!;
                    // no single-fetch on quote openings — list by id within the alternative.
                    const list = await api.quoteOpenings.list({
                        project: props.projectId,
                        alternative,
                        ids: openingId.toString(),
                    });
                    if (list.data.length === 0) {
                        throw new Error("Opening not found");
                    }

                    // the elevation models only read fields QuoteOpening shares with Opening
                    // (id/frameProfile/handing/openingType/…); the Opening-only columns (drw*
                    // links, wood, status) are never read here, so the cast is safe.
                    opening = list.data[0] as unknown as Opening;
                    hardware = opening.hardwareGroup
                        ? (await api.quoteHardware.listWithContents({groups: [opening.hardwareGroup]}))[0] ?? null
                        : null;
                    threePart = await api.elevationTool.getForQuoteOpening({
                        project: props.projectId,
                        quoteOpening: openingId,
                    });
                    this.alternative = alternative;
                } else {
                    const listP = api.openings.getByIds({
                        project: props.projectId,
                        ids: [openingId]
                    })

                    const hardwareP = api.hardware.singleWithContent({
                        opening: openingId,
                    })

                    const list = await listP;
                    hardware = await hardwareP;
                    if (list.length === 0) {
                        throw new Error("Opening not found");
                    }
                    opening = list[0];
                    threePart = await api.elevationTool.getForOpening({
                        project: props.projectId,
                        opening: openingId,
                    });
                    this.alternative = null;
                }

                this.editGlobal = null;
                if(props.editGlobal) {
                    const global = await api.elevationTool.getGlobalDoor({ddl: props.editGlobal});
                    this.editGlobal = {ddl: global.ddl, name: global.name};
                    threePart = {...threePart, door: global.door};
                }

                this.project = pr;
                this.opening = opening;
                this.hardwareGroup = hardware;
                this.anchorProducts.emit(await anchorProductsP);

                this.secondaryOf.emit(opening.drwScreenPrimaryOpening ? {
                    primary: opening.drwScreenPrimaryOpening,
                    primaryName: opening.drwScreenPrimaryOpeningName || "",
                    slot: opening.drwScreenSlot || "",
                } : null);
                this.pendingSlotLinks.emit(new Map());
                await this.loadSlotOpenings();

                const sys = this.project.unitSystem;

                this.frameSpec = threePart.screen
                    ? screenFromData(threePart.screen.dataJson, this.opening, this.hardwareGroup, sys)
                    : MdFrameSpec.fromOpening(this.opening, this.hardwareGroup, sys, this.defaultAnchorProduct(this.opening));
                this.bindSlotOpenings();
                await this.loadSlotDoors();

                this.doorSpec = threePart.door
                    ? doorFromData(threePart.door.dataJson, this.opening, this.hardwareGroup, sys, this.frameSpec)
                    : MdDoorSpec.fromOpening(this.opening, this.hardwareGroup, sys, this.frameSpec);

                // reconcile a loaded hardware elevation against this opening's current
                // hardware group (copy-not-assign): the report drives the review dialog and,
                // when it changed anything, the elevation is marked dirty below so the cleanup
                // persists on save. A fresh (fromOpening) build already matches the group.
                let reconcile: HardwareReconcileReport | null = null;
                if(threePart.hardware) {
                    this.hardware = hardwareFromData(threePart.hardware.dataJson, this.opening, this.hardwareGroup, sys, this.frameSpec, this.doorSpec);
                    reconcile = this.hardware.reconcileToGroup(this, this.hardwareGroup, this.doorSpec);
                } else {
                    this.hardware = MdHardware.fromOpening(this, this.opening, this.hardwareGroup, sys, this.frameSpec, this.doorSpec);
                }

                this.loadedScreen = threePart.screen;
                this.loadedDoor = threePart.door;
                this.loadedHardware = threePart.hardware;
                this.captureLoadedDims();
                this.emitElevationNames();
                this.wireDirtyTracking();

                // wireDirtyTracking resets dirty to false; a reconcile that changed the design
                // must re-dirty so the user saves the cleaned copy, and surface the review.
                if(reconcile?.changed) {
                    this.dirty.emit(true);
                    this.hardwareReconcile.emit(reconcile);
                } else {
                    this.hardwareReconcile.emit(null);
                }

                this.savedDesigns = this.currentDesigns();
                if(reconcile?.changed) {
                    this.savedDesigns.hardware = null;
                }
                if(this.doorSpec.liteIdsRepaired) {
                    this.savedDesigns.door = null;
                    this.dirty.emit(true);
                }

                // undo baseline: after reconcile, so undo can't resurrect placements
                // the reconcile dropped.
                this.history.reset();

                // FrameProfileScene renders dimension labels as MSDF BitmapText,
                // which throws if the font isn't loaded yet.
                await this.draw.waitFonts();

                // land on the workspace's focused view (and set its ribbon filter). The
                // scenes read viewSettings.workspace as they build, so set it first.
                this.viewSettings.workspace.emit(props.workspace);
                this.showWorkspaceHome(props.workspace);

                // auto-save under the name typed in the picker's "draw new" free-solo option.
                // Only when a spec is actually unlinked/fresh (needsName) — so a refresh with
                // the param still in the URL, after the elevation already exists, is a no-op
                // and never persists unrelated unsaved edits. Guarded so a re-run of load
                // doesn't re-save. A name collision just leaves it unsaved (save returns a
                // nameConflict without throwing); the user then saves manually.
                const autoSaveWhich = workspaceSelection(props.workspace);
                if(props.autoSaveName && this.needsName(autoSaveWhich) && this.autoSaveNameDone !== props.autoSaveName) {
                    this.autoSaveNameDone = props.autoSaveName;
                    try {
                        await this.save({name: props.autoSaveName, which: autoSaveWhich});
                    } catch(e) {
                        console.error("elevation auto-save failed", e);
                    }
                }
            } else {
                throw new Error("No opening selected");
            }

        } catch (e) {
            console.error(e);
            this.loadError = e;
            this.draw.showScene(new ErrorScene(e));
        }

        this.ready.emit(true);
    }

    // the anchor product a new frame starts with: the opening's, else the company default.
    defaultAnchorProduct(opening: Opening): number | null {
        return opening.frameAnchorProduct || this.anchorProducts.value?.defaultProduct || null;
    }

    // load the same opening again in the current workspace, e.g. after its drw links changed
    // outside the tool. The auto-save name is dropped so it can't save again.
    async reload() {
        if(!this.lastLoadProps) return;

        await this.load({
            ...this.lastLoadProps,
            workspace: this.viewSettings.workspace.value,
            autoSaveName: undefined,
        });
    }

    destroy() {
        this.clearDirtyTracking();
        this.history.dispose();
        this.draw.destroy();
    }

    resetFrameSpec() {
        if(!this.opening) throw new Error("missing opening");
        if(!this.project) throw new Error("missing project");

        this.frameSpec = MdFrameSpec.fromOpening(this.opening, this.hardwareGroup, this.project.unitSystem, this.defaultAnchorProduct(this.opening));
        this.bindSlotOpenings();
        this.doorSpec = MdDoorSpec.fromOpening(this.opening, this.hardwareGroup, this.project.unitSystem, this.frameSpec);
        this.hardware = MdHardware.fromOpening(this, this.opening, this.hardwareGroup, this.project.unitSystem, this.frameSpec, this.doorSpec);

        this.wireDirtyTracking();
        this.dirty.emit(true);

        // the reset emits no tracked signals after wiring, so record it explicitly to
        // make it a single undoable step.
        this.history.captureNow();
    }

    // put the selected elevations back to their defaults from the opening (e.g. the
    // screen goes back to a plain frame around the door). The other elevations, the
    // frame profile and the nominal sizes stay as they are. One undoable step.
    resetElevations(which: ElevationSelection) {
        const opening = this.opening;
        const project = this.project;
        const snap = this.snapshot();
        if(!opening || !project || !snap) return;
        const sys = project.unitSystem;

        const frame = MdFrameSpec.fromOpening(opening, this.hardwareGroup, sys, this.defaultAnchorProduct(opening));
        if(frame.profile !== this.frameSpec.profile) frame.setProfile(this.frameSpec.profile);
        const door = MdDoorSpec.fromOpening(opening, this.hardwareGroup, sys, frame);
        const hardware = MdHardware.fromOpening(this, opening, this.hardwareGroup, sys, frame, door);

        const identity = this.liveIdentity(opening);
        if(which.screen) snap.screen = screenToData(frame, identity, opening);
        if(which.door) snap.door = doorToData(door, identity, opening);
        if(which.hardware) snap.hardware = hardwareToData(hardware, identity, opening);

        this.restoreSnapshot(snap);
        this.history.captureNow();
    }

    // replace the frame's design with `design` (a Copy Raw dump's `design`, or just the
    // fields to change, over the current design), for debugging a layout someone else
    // made. The door and hardware stay. One undoable step.
    loadFrameDesign(design: Partial<FrameDesign>) {
        const snap = this.snapshot();
        if(!snap) return;

        snap.screen = {...snap.screen, frame: {...snap.screen.frame, ...design}};
        this.restoreSnapshot(snap);
        this.history.captureNow();
    }

    // the tool owns some Synced identity fields live (frameProfile via setProfile), so the
    // serialized identity is the opening's identity with those overlaid — not the stale
    // opening columns. The backend writes this back onto the linked opening(s).
    private liveIdentity(opening: Opening) {
        return {
            ...identityFromOpening(opening),
            frameProfile: this.frameSpec.profile,
        };
    }

    // the live models' designs, keyed like savedDesigns. Leaves out the opening-derived
    // dimensionFingerprint: it follows the opening, not the user's edits to the elevation.
    // Only the screen's identity is compared; the door/hardware copies follow the opening.
    private currentDesigns(): ElevationDesigns {
        const opening = this.opening;
        if(!opening) return {screen: null, door: null, hardware: null};

        const identity = this.liveIdentity(opening);
        return {
            screen: JSON.stringify({identity, design: this.frameSpec.toDesign()}),
            door: JSON.stringify(this.doorSpec.toDesign()),
            hardware: JSON.stringify(this.hardware.toDesign()),
        };
    }

    // the elevations whose live design differs from the last load/save. In edit-global mode only
    // the door is saved, so only the door counts.
    unsavedSelection(): ElevationSelection {
        const current = this.currentDesigns();
        const saved = this.savedDesigns;
        const door = saved.door === null || current.door !== saved.door;
        if(this.editGlobal) return {screen: false, door, hardware: false};

        return {
            // a secondary opening's frame is its primary's, so it never saves a screen.
            screen: !this.secondaryOf.value && (saved.screen === null || current.screen !== saved.screen || this.pendingSlotLinkList().length > 0),
            door,
            hardware: saved.hardware === null || current.hardware !== saved.hardware,
        };
    }

    // the linked door when this project owns it. A global door is never updated from a project,
    // so an edit to it saves as a new door that needs a name.
    private ownDoor(): DoorElevation | null {
        return this.loadedDoor?.global ? null : this.loadedDoor;
    }

    // drops an unchanged global door from a save, so the opening keeps its link to it.
    private withoutUnchangedGlobalDoor(which: ElevationSelection): ElevationSelection {
        if(which.door && this.loadedDoor?.global && !this.unsavedSelection().door) {
            return {...which, door: false};
        }

        return which;
    }

    // true when any elevation or a nominal size differs from the last load/save.
    hasUnsavedChanges() {
        const unsaved = this.unsavedSelection();
        return unsaved.screen || unsaved.door || unsaved.hardware || this.nominalDimensions() !== null;
    }

    // true when an elevation in `which` or a nominal size differs from the last load/save.
    hasUnsavedChangesIn(which: ElevationSelection) {
        const unsaved = this.unsavedSelection();
        return (which.screen && unsaved.screen) || (which.door && unsaved.door)
            || (which.hardware && unsaved.hardware) || this.nominalDimensions() !== null;
    }

    // serialize the live models into an undo snapshot, mirroring save()'s identity
    // overlay and serialization.
    private snapshot(): ElevationSnapshot | null {
        const opening = this.opening;
        if(!opening || !this.project) return null;

        const identity = this.liveIdentity(opening);

        return {
            screen: screenToData(this.frameSpec, identity, opening),
            door: doorToData(this.doorSpec, identity, opening),
            hardware: hardwareToData(this.hardware, identity, opening),
            dimensions: this.currentDims(),
        };
    }

    // rebuild the three models from an undo snapshot, following load()'s order so the
    // door re-shares the frame's FrmVars by reference and hardware wires against both.
    // Skips reconcileToGroup: snapshots come from an already-reconciled live model.
    private restoreSnapshot(snap: ElevationSnapshot) {
        const opening = this.opening;
        const project = this.project;
        if(!opening || !project) return;
        const sys = project.unitSystem;

        this.frameSpec = screenFromData(snap.screen, opening, this.hardwareGroup, sys);
        this.bindSlotOpenings();
        // frameProfile lives on the envelope identity, not FrameDesign, and fromOpening
        // read the possibly-stale opening column. Plain assignment, not setProfile() —
        // setProfile resets the cut vars just restored to the profile defaults.
        this.frameSpec.profile = snap.screen.identity.frameProfile as MdFrameProfileType;
        // before the door/hardware models are built off this frame: fromOpening seeded the
        // nominal sizes from the (possibly stale) opening columns, and the door leaf size +
        // hinge auto-spacing derive from them.
        this.applyDims(snap.dimensions);
        this.doorSpec = doorFromData(snap.door, opening, this.hardwareGroup, sys, this.frameSpec);
        this.hardware = hardwareFromData(snap.hardware, opening, this.hardwareGroup, sys, this.frameSpec, this.doorSpec);

        this.wireDirtyTracking();
        this.dirty.emit(this.hasUnsavedChanges());

        this.rebuildActiveScene();
    }

    // (re)subscribe the dirty flag and undo capture to the current specs' coarse change
    // events. Specs are rebuilt on load/reset/undo, so clear any prior subscriptions
    // first, then reset to clean.
    private wireDirtyTracking() {
        this.clearDirtyTracking();

        const onEdit = () => {
            this.dirty.emit(true);
            this.history.scheduleCapture();
        };

        // add/remove of placements, lites, cells, hinges (these don't go through a FrmVar).
        this.dirtyUnsubs.push(this.frameSpec.frameBlocksChanged.subscribe(onEdit));
        this.dirtyUnsubs.push(this.doorSpec.litesChanged.subscribe(onEdit));
        this.dirtyUnsubs.push(this.hardware.listChanged.subscribe(onEdit));
        this.dirtyUnsubs.push(this.hardware.hinges.listChanged.subscribe(onEdit));

        // every value edit (drag / dimension commit) across all specs. Subscribed after
        // load, so construction-time userSet calls (e.g. applyGroupConfig) don't count.
        const anyEdit = FrmVar.anyUserSet.subscribe(onEdit);
        this.dirtyUnsubs.push(() => anyEdit.cancel());

        this.dirty.emit(false);
    }

    private clearDirtyTracking() {
        this.dirtyUnsubs.forEach(unsub => unsub());
        this.dirtyUnsubs = [];
    }

    // the nominal sizes the frame spec currently holds, in drawing units.
    private currentDims(): SnapshotDimensions {
        return {
            nominalWidth: this.frameSpec.nominalWidth.get(),
            inActiveDoorWidth: this.frameSpec.inActiveDoorWidth.get(),
            nominalHeight: this.frameSpec.nominalHeight.get(),
        };
    }

    // put the nominal sizes back on a freshly-rebuilt frame spec (undo/redo). setInitial
    // rather than userSet: the modified flags aren't load-bearing on these three, and the
    // emit is what makes the calculated dependents (leaf widths, door height) re-derive.
    private applyDims(dims: SnapshotDimensions) {
        this.frameSpec.nominalWidth.setInitial(dims.nominalWidth, true);
        this.frameSpec.inActiveDoorWidth.setInitial(dims.inActiveDoorWidth, true);
        this.frameSpec.nominalHeight.setInitial(dims.nominalHeight, true);
    }

    // snapshot the current nominal sizes as the baseline nominalDimensions() diffs against.
    private captureLoadedDims() {
        this.loadedDims = this.currentDims();
    }

    // the nominal sizes the user changed since load, formatted for the opening's columns —
    // only the changed ones; null when nothing changed. The three vars map 1:1 onto the
    // columns (see MdFrameSpec.nominalWidth), so no conversion is needed.
    nominalDimensions(): ElevationDimensions | null {
        const base = this.loadedDims;
        if(!base || !this.project || this.editGlobal) return null;

        const fs = this.frameSpec;
        const sys = this.project.unitSystem;

        const widthChanged = fs.nominalWidth.get() !== base.nominalWidth;
        const inactiveChanged = fs.isPair && fs.inActiveDoorWidth.get() !== base.inActiveDoorWidth;
        const heightChanged = fs.nominalHeight.get() !== base.nominalHeight;
        if(!widthChanged && !inactiveChanged && !heightChanged) return null;

        return {
            nominalWidth: widthChanged ? formatDim(fs.nominalWidth.get(), sys) : "",
            inactiveDoorWidth: inactiveChanged ? formatDim(fs.inActiveDoorWidth.get(), sys) : "",
            nominalHeight: heightChanged ? formatDim(fs.nominalHeight.get(), sys) : "",
        };
    }

    // ask the server what a save of `which` would stop and ask about — a name collision, a
    // shared-elevation identity fan-out, a nominal-dimension write-back — so the tool can
    // prompt for all of them on one dialog. `name` is the name a not-yet-saved elevation
    // would be created under (the prompt's current value, or the default before it opens).
    async preflight(props: {name: string; which: ElevationSelection}): Promise<SavePreflight> {
        const opening = this.opening;
        const project = this.project;
        if(!opening || !project) throw new Error("nothing loaded");

        const {name} = props;
        const which = this.withoutUnchangedGlobalDoor(props.which);
        const alternative = this.alternative;
        const isQuote = alternative != null;

        const slot = (selected: boolean, loaded: {id: number; name: string} | null): ElevationSaveSlot | null => {
            if(!selected) return null;
            return {id: loaded?.id ?? 0, name: loaded?.name || name};
        };

        return await api.elevationTool.savePreflight({
            project: project.id,
            alternative: alternative,
            setForOpening: isQuote ? null : opening.id,
            setForQuoteOpening: isQuote ? opening.id : null,
            screen: slot(which.screen, this.loadedScreen),
            door: slot(which.door, this.ownDoor()),
            hardware: which.hardware ? {
                ...slot(true, this.loadedHardware)!,
                dataJson: hardwareToData(this.hardware, this.liveIdentity(opening), opening),
            } : null,
            identity: this.liveIdentity(opening),
            dimensions: this.nominalDimensions(),
            secondarySlots: which.screen ? this.frameSpec.secondarySlotNames() : [],
        });
    }

    // pre-fill value for the first-save name prompt.
    defaultElevationName() {
        if(!this.opening) return "";
        return this.opening.name || ("Opening " + this.opening.id);
    }

    // true when saving the selected elevations would create a drw row that has no name
    // yet (a selected spec not already linked). The caller prompts for a name and passes
    // it to save().
    needsName(selection: ElevationSelection) {
        const which = this.withoutUnchangedGlobalDoor(selection);
        return (which.screen && !this.loadedScreen)
            || (which.door && !this.ownDoor())
            || (which.hardware && !this.loadedHardware);
    }

    // the selected elevations' naming state for the name prompt (which need a name vs
    // which keep an existing one). Frame == screen.
    elevationNamingList(selection: ElevationSelection): ElevationNamingItem[] {
        const which = this.withoutUnchangedGlobalDoor(selection);
        const out: ElevationNamingItem[] = [];
        if(which.screen) out.push({kind: "screen", label: "Frame", existingName: this.loadedScreen?.name ?? null});
        if(which.door) out.push({kind: "door", label: "Door", existingName: this.ownDoor()?.name ?? null});
        if(which.hardware) out.push({kind: "hardware", label: "Hardware", existingName: this.loadedHardware?.name ?? null});
        return out;
    }

    // push the current elevation names to subscribers (Save-bar chip). Call after loadedScreen/
    // Door/Hardware change (load, save).
    private emitElevationNames() {
        const door = this.loadedDoor ? this.loadedDoor.name + (this.loadedDoor.global ? " (global)" : "") : null;
        this.elevationNames.emit({
            screen: this.loadedScreen?.name ?? null,
            door: this.editGlobal ? this.editGlobal.name + " (global)" : door,
            hardware: this.loadedHardware?.name ?? null,
        });
    }

    // serialize the selected specs and upsert them, pointing the opening's drw_* links at
    // the results. Specs not already linked are created with `name`; existing rows keep
    // their id + name. Pass a name whenever needsName() is true. `which` selects the
    // elevations to save (default: all three); the unselected ones are sent as null so
    // the backend leaves them (and the opening's link to them) untouched.
    //
    // Nominal sizes the user edited ride along and are written onto this opening;
    // applyDimensionsTo lists the other openings the user opted into on the write-back
    // prompt (see nominalDimensions / preflight).
    //
    // `renames` gives the elevations saved for this opening only, each under its own name, so
    // the other openings keep the shared rows.
    async save(props?: {
        name?: string;
        confirm?: boolean;
        identityResolution?: "update-all" | "detach";
        applyDimensionsTo?: number[];
        dimensionsConfirmed?: boolean;
        hingeSplitConfirmed?: boolean;
        slotUnlinkConfirmed?: boolean;
        which?: ElevationSelection;
        renames?: ElevationRenames;
    }) {
        const opening = this.opening;
        const project = this.project;
        if(!opening || !project) throw new Error("nothing loaded");

        if(this.editGlobal) throw new Error("use saveGlobalDoor in edit-global mode");

        const which = {...this.withoutUnchangedGlobalDoor(props?.which ?? {screen: true, door: true, hardware: true})};
        // a secondary opening's frame is its primary's: saving a screen here would take it off
        // that frame (see drw linkOpening).
        if(this.secondaryOf.value) which.screen = false;
        if(!which.screen && !which.door && !which.hardware) {
            this.dirty.emit(this.hasUnsavedChanges());
            return {};
        }

        const renames = props?.renames ?? {};

        const name = props?.name?.trim() || "";
        if(this.needsName(which) && !name) throw new Error("name required");

        const target = (loaded: {id: number; name: string} | null, rename: ElevationRename | undefined) => {
            if(rename) {
                const renamed = rename.name.trim();
                if(!renamed) throw new Error("name required");
                return {id: rename.newRow || !loaded ? 0 : loaded.id, name: renamed};
            }
            if(!loaded) return {id: 0, name: name};
            return {id: loaded.id, name: loaded.name || name};
        };
        const screenTarget = target(this.loadedScreen, renames.screen);
        const doorTarget = target(this.ownDoor(), renames.door);
        const hardwareTarget = target(this.loadedHardware, renames.hardware);

        const identity = this.liveIdentity(opening);

        // the screen's identity is the one the backend writes to the opening. The door/hardware
        // blobs carry a copy, so they take what the opening will hold after this save — not an
        // identity edit the screen isn't saving.
        const copyIdentity = which.screen ? identity : identityFromOpening(opening);

        // quote mode targets a quote opening in its alternative's scope; production mode
        // targets a production opening in project scope (see this.alternative).
        const alternative = this.alternative;
        const isQuote = alternative != null;

        const dimensions = this.nominalDimensions();

        if(which.screen) this.stampSlotOpenings();

        // captured before the request: edits made while it runs must stay unsaved.
        const sentDesigns = this.currentDesigns();
        const sentDims = this.currentDims();

        const result = await api.elevationTool.upsertThreePart({
            project: project.id,
            alternative: alternative,
            setForOpening: isQuote ? null : opening.id,
            setForQuoteOpening: isQuote ? opening.id : null,
            confirm: props?.confirm,
            identityResolution: props?.identityResolution,
            dimensions: dimensions,
            applyDimensionsTo: props?.applyDimensionsTo,
            dimensionsConfirmed: props?.dimensionsConfirmed,
            hingeSplitConfirmed: props?.hingeSplitConfirmed,
            slotUnlinkConfirmed: props?.slotUnlinkConfirmed,
            slotLinks: which.screen ? this.pendingSlotLinkList() : undefined,
            screen: which.screen ? {
                id: screenTarget.id,
                project: project.id,
                alternative: alternative,
                name: screenTarget.name,
                dataJson: screenToData(this.frameSpec, identity, opening),
                archived: false,
            } : null,
            door: which.door ? {
                id: doorTarget.id,
                project: project.id,
                alternative: alternative,
                name: doorTarget.name,
                dataJson: doorToData(this.doorSpec, copyIdentity, opening),
                archived: false,
            } : null,
            hardware: which.hardware ? {
                id: hardwareTarget.id,
                project: project.id,
                alternative: alternative,
                name: hardwareTarget.name,
                dataJson: hardwareToData(this.hardware, copyIdentity, opening),
                archived: false,
            } : null,
        });

        // the name collided with an existing elevation and confirm wasn't set: nothing was
        // saved. Surface the conflict so the caller can prompt and re-save with confirm.
        if(result.nameConflict) {
            return {nameConflict: result.nameConflict};
        }

        // an identity edit hit a shared elevation and no resolution was chosen: nothing was
        // saved. Surface it so the caller can prompt (Update all / Detach / Cancel) and
        // re-save with an identityResolution.
        if(result.sharedIdentityConflict) {
            return {sharedIdentityConflict: result.sharedIdentityConflict};
        }

        // a nominal-dimension edit could be adopted by other openings and the user hasn't
        // picked yet: nothing was saved. Surface it so the caller can prompt and re-save with
        // dimensionsConfirmed.
        if(result.dimensionChange) {
            return {dimensionChange: result.dimensionChange};
        }

        // the hinge layout needs openings moved out of their hardware group and the user hasn't
        // agreed yet: nothing was saved. Surface it so the caller can prompt and re-save with
        // hingeSplitConfirmed.
        if(result.hingeGroupSplit) {
            return {hingeGroupSplit: result.hingeGroupSplit};
        }

        // the screen drops door slots that openings fill and the user hasn't agreed yet: nothing
        // was saved. Surface it so the caller can prompt and re-save with slotUnlinkConfirmed.
        if(result.slotUnlink) {
            return {slotUnlink: result.slotUnlink};
        }

        // only adopt the results for the elevations we actually saved — the unselected
        // ones came back null (not touched) and must keep their loaded rows.
        if(which.screen) {
            this.loadedScreen = result.screen;
            this.pendingSlotLinks.emit(new Map());
            await this.loadSlotOpenings();
            await this.loadSlotDoors();
        }
        if(which.door) this.loadedDoor = result.door;
        if(which.hardware) this.loadedHardware = result.hardware;
        this.emitElevationNames();

        // re-baseline only what was written; the unselected elevations keep their changes.
        if(which.screen) this.savedDesigns.screen = sentDesigns.screen;
        if(which.door) this.savedDesigns.door = sentDesigns.door;
        if(which.hardware) this.savedDesigns.hardware = sentDesigns.hardware;

        // keep the in-memory opening consistent with what was persisted, so a later
        // fromOpening-based reset reads the profile the user just saved. Only the
        // screen carries the identity to the opening.
        if(which.screen) opening.frameProfile = identity.frameProfile;

        // same for the nominal sizes, and re-baseline them so a second save doesn't re-offer
        // the write-back for an edit that's already persisted.
        if(dimensions) {
            if(dimensions.nominalWidth) opening.nominalWidth = dimensions.nominalWidth;
            if(dimensions.inactiveDoorWidth) opening.inactiveDoorWidth = dimensions.inactiveDoorWidth;
            if(dimensions.nominalHeight) opening.nominalHeight = dimensions.nominalHeight;
            this.loadedDims = sentDims;
        }

        this.dirty.emit(this.hasUnsavedChanges());
        return {};
    }

    // save the door as the global door elevation being edited (edit-global mode). rerender also
    // updates the openings that link it; else only the drawing changes.
    async saveGlobalDoor(props: {rerender: boolean}) {
        const opening = this.opening;
        const editGlobal = this.editGlobal;
        if(!opening || !editGlobal) throw new Error("not in edit-global mode");

        // captured before the request: edits made while it runs must stay unsaved.
        const sentDesigns = this.currentDesigns();

        const result = await api.elevationTool.upsertGlobalDoor({
            ddl: editGlobal.ddl,
            dataJson: doorToData(this.doorSpec, identityFromOpening(opening), opening),
            rerender: props.rerender,
        });

        this.loadedDoor = result.door;
        this.emitElevationNames();
        this.savedDesigns.door = sentDesigns.door;
        this.dirty.emit(this.hasUnsavedChanges());
    }

    // single chokepoint for switching scenes. Clears any canvas selection first:
    // a selected element (stick/lite/panel) belongs to the outgoing scene, so its
    // contextual "Selected" tab must not carry over to the new one.
    navToScene(route: Scene) {
        this.viewSettings.clearSelection();
        this.draw.showScene(route);
    }

    // switches the active "display" view shown on the canvas and records it in
    // viewSettings so the ribbon stays in sync.
    showView(view: MdDisplayView) {
        this.viewSettings.activeView.emit(view);
        // land on the workspace tab so the new scene's editing tools are at hand.
        this.viewSettings.activeTab.emit("workspace");

        const scene = this.sceneFor(view);
        if(scene) this.navToScene(scene);
    }

    // build a fresh scene for a display view. null for the export views — those go
    // through showExport.
    private sceneFor(view: MdDisplayView): Scene | null {
        switch(view) {
            case "frame-profile-active":
                return new FrameProfileScene(this, true);
            case "frame-profile-inactive":
                return new FrameProfileScene(this, false);
            case "top-down":
                return new TopDownScene(this);
            case "frame-grid":
                return new FrameGridScene(this);
            case "all":
                return new FrameAllScene(this);
            case "door-active":
                return new DoorElevScene(this, true);
            case "door-inactive":
                return new DoorElevScene(this, false);
            case "location-active":
                return new LocationsScene(this, true);
            case "location-inactive":
                return new LocationsScene(this, false);
            default:
                return null;
        }
    }

    // rebuild the currently-active scene against the current model instances without
    // forcing the workspace tab (showView would). Used after undo/redo swaps the models:
    // the outgoing scene's subscriptions point at the old instances, and navToScene
    // clears the selection, whose ids belong to that outgoing scene.
    private rebuildActiveScene() {
        const view = this.viewSettings.activeView.value;
        if(view === "screen-export") { this.showExport("screen"); return; }
        if(view === "door-export") { this.showExport("door"); return; }
        if(view === "hardware-export") { this.showExport("hardware"); return; }
        if(view === "cutplan-export") { this.showExport("cutplan"); return; }

        const scene = this.sceneFor(view);
        if(scene) this.navToScene(scene);
    }

    // land on the workspace's home view. Sets activeView so the ribbon stays in sync,
    // then builds the scene directly.
    showWorkspaceHome(workspace: Workspace) {
        const view = workspaceHomeView(workspace);
        this.viewSettings.activeView.emit(view);
        this.viewSettings.activeTab.emit("workspace");

        switch(view) {
            case "door-active":
                this.draw.showScene(new DoorElevScene(this, true));
                break;
            case "location-active":
                this.draw.showScene(new LocationsScene(this, true));
                break;
            default:
                this.draw.showScene(new FrameAllScene(this));
        }
    }

    // the openings on this opening's frame and the ones that could fill a door slot. A secondary
    // opening has no frame of its own, so it gets none.
    async loadSlotOpenings() {
        const opening = this.opening;
        const project = this.project;
        if(!opening || !project || this.secondaryOf.value) {
            this.slotOpenings.emit(null);
            return;
        }

        const isQuote = this.alternative != null;
        this.slotOpenings.emit(await api.elevationTool.listSlotOpenings({
            project: project.id,
            opening: isQuote ? undefined : opening.id,
            quoteOpening: isQuote ? opening.id : undefined,
        }));

        this.bindSlotOpenings();
    }

    // size the secondary door cells from the openings that fill them (see wireDoorsItem).
    bindSlotOpenings() {
        const sys = this.project?.unitSystem ?? "imperial";
        this.frameSpec.slotOpeningSize = (slot: string) => {
            const opening = this.slotOpening(slot);
            if(!opening) return null;

            const dims = opening.dimensions;
            const nominalWidth = parseDrawingUnits(dims.nominalWidth, sys);
            const inactiveWidth = opening.identity.openingType === "Pair" ? parseDrawingUnits(dims.inactiveDoorWidth, sys) : 0;
            return {width: nominalWidth + inactiveWidth, nominalHeight: parseDrawingUnits(dims.nominalHeight, sys)};
        };
        wireDoorsItem(this.frameSpec);
    }

    // store each slot's opening identity and size on the slot, for the saved screen. An empty
    // slot keeps none.
    private stampSlotOpenings() {
        for(const slot of this.frameSpec.slots) {
            if(slot.name === this.frameSpec.primarySlot) continue;

            const opening = this.slotOpening(slot.name);
            slot.identity = opening?.identity;
            slot.dimensionFingerprint = opening?.dimensions;
        }
    }

    // the slot names the screen held at the last load or save. They can't be renamed: openings hold
    // them by name.
    savedSlotNames(): Set<string> {
        return new Set((this.loadedScreen?.dataJson?.frame?.slots ?? []).map(s => s.name));
    }

    // the door slots a screen save would store for the first time, with the opening that fills each.
    newSlots(): {name: string; opening: string | null}[] {
        const saved = this.savedSlotNames();
        return this.frameSpec.slots
            .filter(s => !saved.has(s.name))
            .map(s => ({
                name: s.name,
                opening: s.name === this.frameSpec.primarySlot ? (this.opening?.name ?? null) : (this.slotOpening(s.name)?.name ?? null),
            }));
    }

    // the opening that fills a door slot: one picked since the last save, else the linked one.
    slotOpening(slot: string): SlotOpening | null {
        const picked = this.pendingSlotLinks.value.get(slot);
        if(picked) return picked;
        return this.slotOpenings.value?.linked.find(o => o.slot === slot) ?? null;
    }

    // pick the opening for a door slot; it joins the frame on the next screen save.
    async pickSlotOpening(slot: string, opening: SlotOpening) {
        const next = new Map(this.pendingSlotLinks.value);
        next.set(slot, opening);
        this.pendingSlotLinks.emit(next);
        this.bindSlotOpenings();
        this.dirty.emit(true);
        this.frameSpec.frameBlocksChanged.emit(true);

        await this.loadSlotDoors();
        this.frameSpec.frameBlocksChanged.emit(false);
    }

    // build the door drawn in each filled secondary slot, keeping the ones whose opening hasn't
    // changed.
    async loadSlotDoors() {
        const project = this.project;
        if(!project) return;

        const next = new Map<string, SlotDoor>();
        for(const slot of this.frameSpec.secondarySlotNames()) {
            const opening = this.slotOpening(slot);
            if(!opening) continue;

            const cached = this.slotDoors.get(slot);
            const door = cached && cached.openingId === opening.id ? cached : await this.buildSlotDoor(project, opening.id);
            if(door) next.set(slot, door);
        }

        this.slotDoors = next;
    }

    // the door of a secondary opening: its own frame spec (handing, pair split, gaps) and its door
    // elevation, or a plain door from the opening when it has none. null when the opening is gone.
    private async buildSlotDoor(project: Project, openingId: number): Promise<SlotDoor | null> {
        const sys = project.unitSystem;
        let opening: Opening | undefined;
        let threePart: ThreePartElevation;

        if(this.alternative != null) {
            const list = await api.quoteOpenings.list({project: project.id, alternative: this.alternative, ids: openingId.toString()});
            opening = list.data[0] as unknown as Opening | undefined;
            if(!opening) return null;
            threePart = await api.elevationTool.getForQuoteOpening({project: project.id, quoteOpening: openingId});
        } else {
            opening = (await api.openings.getByIds({project: project.id, ids: [openingId]}))[0];
            if(!opening) return null;
            threePart = await api.elevationTool.getForOpening({project: project.id, opening: openingId});
        }

        const frame = MdFrameSpec.fromOpening(opening, null, sys);
        const door = threePart.door
            ? doorFromData(threePart.door.dataJson, opening, null, sys, frame)
            : MdDoorSpec.fromOpening(opening, null, sys, frame);

        return {openingId, frame, door};
    }

    // the slot picks still on a slot of the frame, for the save request.
    pendingSlotLinkList(): {slot: string; opening: number}[] {
        const slots = new Set(this.frameSpec.secondarySlotNames());
        return Array.from(this.pendingSlotLinks.value.entries())
            .filter(([slot]) => slots.has(slot))
            .map(([slot, opening]) => ({slot, opening: opening.id}));
    }

    // change the focused workspace (ribbon tab). Re-navigates to a view valid
    // for the new workspace — keeping the current view when it's still allowed — so
    // the scene rebuilds under the new filter (e.g. dropping hardware/hinges for
    // "screen").
    setWorkspace(workspace: Workspace) {
        if(this.viewSettings.workspace.value === workspace) return;
        this.viewSettings.workspace.emit(workspace);

        const current = this.viewSettings.activeView.value;
        if(workspaceViews(workspace).includes(current)) {
            this.showView(current);
        } else {
            this.showView(workspaceHomeView(workspace));
        }
    }

    // show a submittal export preview (Debug tab). Records the active view so the
    // ribbon stays in sync but keeps the current tab so the Debug buttons stay put.
    // Returns the scene so the off-screen renderer can read what it measured.
    showExport(kind: ExportKind): ExportScene {
        this.viewSettings.activeView.emit(exportViews[kind]);
        const scene = exportSceneFor(kind, this);
        this.navToScene(scene);
        return scene;
    }

    navTo(route: Route) {
        switch(route) {
            case "frameAll":
                this.navToScene(new FrameAllScene(this));
                break;
            case "frameTopDown":
                this.navToScene(new TopDownScene(this));
                break;
            case "frameProfile":
                this.navToScene(new FrameProfileScene(this, true));
                break;
        }
    }
}

export type Route = "frameAll" | "frameTopDown" | "frameProfile";

export function useElevationApp(props: {
    opening: string;
    // set (with alternative) to run the tool against a quote opening instead of a
    // production opening.
    quoteOpening?: string;
    alternative?: number;
    autoSaveName?: string;
    // the drwDoorElevation ddl id of a global door elevation to edit (edit-global mode).
    editGlobal?: number;
    // open in anchor setup once loaded (from the openings-grid sidebar's Edit Anchors).
    anchorSetup?: boolean;
    workspace: Workspace;
    onWorkspaceChange(workspace: Workspace): void;
}) {
    const projectId = useProjectId();

    const {opening, quoteOpening, alternative, autoSaveName, editGlobal, anchorSetup, workspace, onWorkspaceChange} = props;
    const app = useRef<ElevationApp | null>(null);
    const appEmitter = useMemo(() => new EventEmitter2<ElevationApp|null>(null, "elev.app"), []);

    // the initial load reads the workspace but must not refetch when it changes (a
    // workspace switch only re-navigates the scene, see the effect below). Keep the
    // latest value in a ref so load picks it up without being a dependency.
    const workspaceRef = useRef(workspace);
    workspaceRef.current = workspace;

    const [visible, setVisible] = useState<{raw: () => any}>({raw: () => null});
    const nav = useNavigate();

    const canvas = <StatusHintProvider><div className="drawfrm-root" style={{
        flex: 1,
        display: "flex",
        position: "relative",
        flexDirection: "column",
    }}>
        <MenuBar app={appEmitter} onBack={() => nav(editGlobal
                    ? `/projects/settings?t=elevations&t2=door-elev`
                    : alternative != null
                    ? `/project/${projectId}/quote/${alternative}/openings`
                    : `/project/${projectId}/shop-drawing`)}
                 workspace={workspace} onWorkspaceChange={onWorkspaceChange} />
        <div style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            position: "relative",
        }}>
        <div className="drawfrm-canvas" style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            position: "relative",
        }} ref={r => {
            if(app.current) return;

            if(r) {
                app.current = new ElevationApp(r, {
                    onShowReact: (el: any) => setVisible({raw: el}),
                    onHideReact: () => setVisible({raw: () => null})
                });
                appEmitter.emit(app.current);
                // initial load is driven by the effect below (single loader), so the
                // auto-save on open runs exactly once.
            }
        }}></div>
        <HardwareSidebar app={appEmitter} />
        <DoorSidebar app={appEmitter} />
        </div>

        {visible.raw()}
        <StatusBar app={appEmitter} />
    </div></StatusHintProvider>

    useEffect(() => {
        const current = app.current;
        if(current) {
            current.load({
                projectId,
                opening,
                quoteOpening,
                alternative,
                autoSaveName,
                editGlobal,
                workspace: workspaceRef.current,
            }).then(() => {
                if(anchorSetup && app.current === current && !current.loadError && anchorSetupAllowed(current)) {
                    startAnchorSetup(current);
                }
            });
        }
    }, [projectId, opening, quoteOpening, alternative, autoSaveName, editGlobal, anchorSetup]);

    // apply a workspace switch (from the ribbon tabs / url) without reloading.
    // load() applies the initial workspace itself, so this only fires for later
    // changes — and it's a no-op until the models are loaded (setWorkspace navigates
    // a scene, which needs them).
    useEffect(() => {
        if(app.current && app.current.ready.value) {
            app.current.setWorkspace(workspace);
        }
    }, [workspace]);

    // ask before a refresh / tab close / navigation to another site while there are unsaved
    // changes. In-app navigation is covered by the Close Editor button's confirm dialog.
    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if(!app.current?.dirty.value) return;
            e.preventDefault();
            e.returnValue = "";
        };

        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, []);

    useEffect(() => {
        return () => {
            if(app.current) {
                setVisible({raw: () => null})
                app.current.destroy();
                app.current = null;
                appEmitter.emit(null);
            }
        }
    }, [appEmitter])

    return {
        canvas,
        app,
    }
}
// the door drawn in a secondary door slot (multi-opening screens): the filling opening's own frame
// spec and door spec. Read-only; see DoorSlotFace.
export type SlotDoor = {
    openingId: number;
    frame: MdFrameSpec;
    door: MdDoorSpec;
};
